// src/pages/Perfil.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { FaArrowLeft, FaKey, FaSave, FaUserEdit, FaVolumeUp } from 'react-icons/fa';
import PrivacyPolicyLite from '../components/PrivacyPolicyLite';
import './Perfil.css';

const Perfil = () => {
    const { currentUser, userData, loading } = useAuth();
    const { showNotification } = useNotification();

    const [form, setForm] = useState({
        nombre: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        telefono: '',
        genero: '',
        fechaNacimiento: '',
        sonidosHabilitados: true,
    });
    const [saving, setSaving] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);

    const email = currentUser?.email || '';

    const nombreCompleto = useMemo(() => {
        const parts = [
            form.nombre?.trim(),
            form.apellidoPaterno?.trim(),
            form.apellidoMaterno?.trim(),
        ].filter(Boolean);
        if (parts.length > 0) {
            return parts.join(' ').trim();
        }
        return currentUser?.displayName || userData?.nombreCompleto || email || '';
    }, [currentUser?.displayName, email, form.apellidoMaterno, form.apellidoPaterno, form.nombre, userData?.nombreCompleto]);

    useEffect(() => {
        if (!currentUser && !userData) {
            return;
        }

        const portalParts = userData?.portalNombrePartes || {};
        const initialNombre = portalParts.nombre || userData?.nombre || userData?.nombres || currentUser?.displayName || '';
        const initialApP = portalParts.apellidoPaterno || userData?.apellidoPaterno || '';
        const initialApM = portalParts.apellidoMaterno || userData?.apellidoMaterno || '';
        const initialTel = userData?.portalTelefono || userData?.telefono || userData?.celular || '';
        const initialGenero = userData?.portalGenero || userData?.genero || '';
        const initialFechaNac = userData?.portalFechaNacimiento || userData?.fechaNacimiento || '';

        setForm({
            nombre: initialNombre,
            apellidoPaterno: initialApP,
            apellidoMaterno: initialApM,
            telefono: initialTel,
            genero: initialGenero,
            fechaNacimiento: initialFechaNac,
            sonidosHabilitados: userData?.preferencias?.sonidosHabilitados !== false,
        });
    }, [currentUser, userData]);

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validatePhone = (phone) => {
        if (!phone) return true;
        return /^\d{10}$/.test(phone.trim());
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!currentUser) {
            showNotification('Inicia sesión nuevamente para editar tu perfil.', 'error');
            return;
        }

        if (!form.nombre.trim()) {
            showNotification('El nombre es requerido.', 'warning');
            return;
        }

        const phone = form.telefono.trim();
        if (!validatePhone(phone)) {
            showNotification('El teléfono debe tener 10 dígitos (solo números).', 'warning');
            return;
        }

        // Guardar SOLO para vista del portal sin cambiar Auth/Firebase core
        const payload = {
            portalNombre: nombreCompleto || null,
            portalNombrePartes: {
                nombre: form.nombre.trim() || null,
                apellidoPaterno: form.apellidoPaterno.trim() || null,
                apellidoMaterno: form.apellidoMaterno.trim() || null,
            },
            portalTelefono: phone || null,
            portalGenero: (form.genero || '').trim() || null,
            portalFechaNacimiento: (form.fechaNacimiento || '').trim() || null,
            preferencias: {
                ...userData?.preferencias,
                sonidosHabilitados: form.sonidosHabilitados,
            },
        };

        try {
            setSaving(true);
            const userRef = doc(db, 'usuarios', currentUser.uid);
            await setDoc(userRef, payload, { merge: true });
            showNotification('Perfil actualizado correctamente.', 'success');
        } catch (error) {
            console.error('Perfil: no se pudo actualizar', error);
            showNotification('No pudimos guardar tu perfil. Intenta de nuevo.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleResetPassword = async () => {
        if (!currentUser?.email) {
            showNotification('No encontramos tu correo para enviar el enlace.', 'error');
            return;
        }

        try {
            setSendingReset(true);
            await sendPasswordResetEmail(auth, currentUser.email);
            showNotification('Te enviamos un correo para cambiar tu contraseña.', 'success');
        } catch (error) {
            console.error('Perfil: no se pudo enviar reset de contraseña', error);
            showNotification('No pudimos enviar el enlace. Intenta más tarde.', 'error');
        } finally {
            setSendingReset(false);
        }
    };

    const disableForm = loading || saving;

    return (
        <div className="config-page profile-page">
            {/* Header principal fuera de la tarjeta */}
            <div className="profile-header-outside">
                <p>Actualiza tu nombre y teléfono. El correo se usa para iniciar sesión.</p>
            </div>

            <div className="profile-card-container">
                <div className="profile-main-card">
                    {/* Header de la tarjeta con botón volver */}
                    <div className="profile-card-header">
                        <div className="profile-card-title-area">
                            <h3><FaUserEdit /> Datos de cuenta</h3>
                            <p className="profile-card-subtitle">Estos datos aparecen en reportes, visitas y notificaciones.</p>
                        </div>
                        <Link to="/configuraciones" className="profile-back-button">
                            <FaArrowLeft /> Volver
                        </Link>
                    </div>

                    <form className="profile-form" onSubmit={handleSubmit}>
                        <div className="form-row full-width">
                            <div className="form-group">
                                <label htmlFor="nombre">Nombre(s)</label>
                                <input
                                    id="nombre"
                                    type="text"
                                    value={form.nombre}
                                    onChange={handleChange('nombre')}
                                    placeholder="Ej. María Elena"
                                    disabled={disableForm}
                                    required
                                    autoCorrect="on"
                                    spellCheck="true"
                                    autoCapitalize="words"
                                />
                            </div>
                        </div>

                        <div className="form-row two-columns">
                            <div className="form-group">
                                <label htmlFor="apellidoPaterno">Apellido paterno</label>
                                <input
                                    id="apellidoPaterno"
                                    type="text"
                                    value={form.apellidoPaterno}
                                    onChange={handleChange('apellidoPaterno')}
                                    placeholder="Ej. Hernández"
                                    disabled={disableForm}
                                    autoCorrect="on"
                                    spellCheck="true"
                                    autoCapitalize="words"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="apellidoMaterno">Apellido materno</label>
                                <input
                                    id="apellidoMaterno"
                                    type="text"
                                    value={form.apellidoMaterno}
                                    onChange={handleChange('apellidoMaterno')}
                                    placeholder="Opcional"
                                    disabled={disableForm}
                                    autoCorrect="on"
                                    spellCheck="true"
                                    autoCapitalize="words"
                                />
                            </div>
                        </div>

                        <div className="form-row two-columns">
                            <div className="form-group">
                                <label htmlFor="genero">Género</label>
                                <select
                                    id="genero"
                                    value={form.genero}
                                    onChange={handleChange('genero')}
                                    disabled={disableForm}
                                >
                                    <option value="">Selecciona una opción</option>
                                    <option value="femenino">Femenino</option>
                                    <option value="masculino">Masculino</option>
                                    <option value="otro">Otro</option>
                                    <option value="prefiero_no_decir">Prefiero no decir</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="fechaNacimiento">Fecha de nacimiento</label>
                                <input
                                    id="fechaNacimiento"
                                    type="date"
                                    value={form.fechaNacimiento}
                                    onChange={handleChange('fechaNacimiento')}
                                    max={new Date().toISOString().split('T')[0]}
                                    disabled={disableForm}
                                />
                            </div>
                        </div>

                        <div className="form-row full-width">
                            <div className="form-group">
                                <label htmlFor="telefono">Teléfono</label>
                                <input
                                    id="telefono"
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={10}
                                    value={form.telefono}
                                    onChange={handleChange('telefono')}
                                    placeholder="10 dígitos"
                                    disabled={disableForm}
                                />
                                <small className="form-hint">Se usa para notificaciones y contacto en registros.</small>
                            </div>
                        </div>

                        <div className="form-row full-width">
                            <div className="form-group">
                                <label htmlFor="correo">Correo</label>
                                <input id="correo" type="email" value={email} disabled />
                                <small className="form-hint">El correo solo cambia mediante soporte o enlace de recuperación.</small>
                            </div>
                        </div>

                        <div className="profile-summary-box">
                            <p><strong>Tu nombre público:</strong> {nombreCompleto || 'Completa tu nombre'}</p>
                        </div>

                        <div className="profile-form-actions">
                            <button type="submit" className="btn-save" disabled={disableForm}>
                                <FaSave /> {saving ? 'Guardando…' : 'Guardar cambios'}
                            </button>
                            <button
                                type="button"
                                className="btn-password"
                                onClick={handleResetPassword}
                                disabled={sendingReset || loading}
                            >
                                <FaKey /> {sendingReset ? 'Enviando…' : 'Cambiar contraseña'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Aviso de Privacidad */}
                <PrivacyPolicyLite userId={currentUser?.uid} />
            </div>
        </div>
    );
};

export default Perfil;
